import { OverlayContainer } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  APP_ID,
  ApplicationConfig,
  ErrorHandler,
  Inject,
  Injectable,
  OnDestroy,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HammerModule, provideClientHydration, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { ApolloModule } from 'apollo-angular';
import { DeviceDetectorService } from 'ngx-device-detector';
import { provideNgxStripe } from 'ngx-stripe';
import { environment } from 'src/environments/environment';
import { APOLLO_PROVIDERS } from 'utils/apollo';
import { COLOR_PROVIDER } from 'utils/color';
import { DATE_PROVIDERS } from 'utils/date-providers';
import { DEVICE_DETECTOR_SERVICE } from 'utils/device-detector';
import { I18N_PROVIDERS } from 'utils/i18n';
import { INIT_ICON_REGISTRY } from 'utils/icon-registry';
import { INIT_USER_PROVIDER } from 'utils/init-user';
import { INIT_FILTERABLES_PROVIDER } from 'utils/search-results-filterable';
import { SharryErrorHandler } from 'utils/sharry-error-handler';
import { routes } from './app.routes';
import { CAPACITOR_SERVICE, CapacitorService } from './common/capacitor/capacitor.service';
import { BrowserCryptoHashService } from './common/crypto-hash/browser-crypto-hash.service';
import { CryptoHashService } from './common/crypto-hash/crypto-hash.service';
import { GlitchtipService } from './common/glitchtip/glitchtip.service';
import { provideSvgIcon } from './common/icon/icon.provider';
import { SvgHttpLoader, SvgLoader } from './common/icon/svg-loader';
import { METADATA_SERVICE, MetadataService } from './common/metadata/metadata.service';
import { requestInterceptorFn } from './common/server-request.interceptor';
import { StorageService } from './common/storage/storage.service';
import { UserService } from './common/user/user.service';
import { MAT_SNACK_BAR, SharryErrorStateMatcher } from './common/utils/helpers';

@Injectable({ providedIn: 'root' })
export class InAppRootOverlayContainer extends OverlayContainer implements OnDestroy {
  constructor(@Inject(DOCUMENT) _document: Document, _platform: Platform) {
    super(_document, _platform);
  }

  ngOnDestroy() {
    this._containerElement?.remove();
  }

  getRootElement(): Element {
    return this._document.querySelector('.app-wrapper');
  }

  getContainerElement(): HTMLElement {
    if (!this._containerElement) {
      this._createContainer();
    }

    return this._containerElement;
  }

  protected _createContainer(): void {
    const containerClass = 'cdk-overlay-container';

    if (this._platform.isBrowser) {
      const oppositePlatformContainers = this._document.querySelectorAll(
        `.${containerClass}[platform="server"], ` + `.${containerClass}[platform="test"]`,
      );

      // Remove any old containers from the opposite platform.
      // This can happen when transitioning from the server to the client.
      for (let i = 0; i < oppositePlatformContainers.length; i++) {
        oppositePlatformContainers[i].remove();
      }
    }

    const container = this._document.createElement('div');
    container.classList.add(containerClass);
    const params = new URLSearchParams(this._document.defaultView?.location.search || '');
    if (params.get('embed') === 'true') {
      container.style.pointerEvents = 'none';
      container.style.zIndex = '0';
    }

    if (!this._platform.isBrowser) {
      container.setAttribute('platform', 'server');
    }
    const rootElement = this.getRootElement();
    const parent = rootElement || this._document.body;
    parent.appendChild(container);
    this._containerElement = container;
  }
}
export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: false,
      }),
    ),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({
        urlUpdateStrategy: 'deferred',
      }),
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      // withViewTransitions({ skipInitialTransition: true }),
      // withDebugTracing(),
    ),
    provideAnimations(),
    provideHttpClient(withFetch(), withInterceptors([requestInterceptorFn])),
    provideNgxStripe(environment.stripeKey),
    INIT_USER_PROVIDER,
    INIT_ICON_REGISTRY,
    INIT_FILTERABLES_PROVIDER,
    ...I18N_PROVIDERS,
    ...APOLLO_PROVIDERS,
    ...DATE_PROVIDERS,
    COLOR_PROVIDER,
    UserService,
    StorageService,
    provideSvgIcon({
      loader: {
        provide: SvgLoader,
        useFactory: (http: HttpClient): SvgHttpLoader => new SvgHttpLoader(http),
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(I18NextModule.forRoot(), ApolloModule, MatDialogModule, HammerModule, MatBottomSheetModule),
    {
      provide: MAT_SNACK_BAR,
      useClass: MatSnackBar,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'auto', appearance: 'outline', color: 'primary' },
    },
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: MAT_DIALOG_DATA,
      useValue: [],
    },
    { provide: ErrorStateMatcher, useClass: SharryErrorStateMatcher },
    {
      provide: OverlayContainer,
      useClass: InAppRootOverlayContainer,
    },
    {
      provide: ErrorHandler,
      useClass: SharryErrorHandler,
      deps: [GlitchtipService],
    },
    {
      provide: DEVICE_DETECTOR_SERVICE,
      useClass: DeviceDetectorService,
    },
    {
      provide: CAPACITOR_SERVICE,
      useClass: CapacitorService,
    },
    {
      provide: METADATA_SERVICE,
      useClass: MetadataService,
    },
    // {
    //   provide: ROUTER_SCROLL_SERVICE,
    //   useClass: RouterScrollService,
    // },
    {
      provide: APP_ID,
      useValue: 'sharryland-frontend',
    },
    { provide: CryptoHashService, useClass: BrowserCryptoHashService },
  ],
};
